import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { AuthAside } from "@/components/AuthAside";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, KeyRound } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/password/forgot", { email });
      setDevCode(data?.dev_code || "");
      setStep(2);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/password/reset", { email, code, new_password: newPassword });
      toast.success("Password reset — please log in.");
      navigate("/login");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="forgot-page">
      <AuthAside titleKey="auth.grindTitle" subKey="auth.grindSub" />

      <div className="flex items-center justify-center p-6 sm:p-12 relative">
        <div className="absolute top-5 right-5"><LanguageToggle /></div>
        <motion.div key={step} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo size={38} textClass="text-2xl" /></div>
          <div className="h-12 w-12 rounded-2xl emerald-surface grid place-items-center mb-4"><KeyRound className="h-6 w-6 text-white" /></div>

          {step === 1 ? (
            <>
              <h1 className="font-display text-3xl font-semibold mb-1">{t("auth.forgotTitle")}</h1>
              <p className="text-muted-foreground mb-8 text-sm">{t("auth.forgotSub")}</p>
              <form onSubmit={requestCode} className="space-y-4" data-testid="forgot-request-form">
                <div>
                  <label className="text-sm font-medium">{t("auth.email")}</label>
                  <input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="mt-1.5 w-full h-11 px-4 rounded-xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-ring" placeholder="you@example.com" />
                </div>
                {error && <div data-testid="forgot-error" className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</div>}
                <button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                  className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("auth.sendCode")} <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl font-semibold mb-1">{t("auth.resetTitle")}</h1>
              <p className="text-muted-foreground mb-6 text-sm">{t("auth.resetSub")}</p>
              {devCode && (
                <div className="mb-4 rounded-xl bg-accent text-accent-foreground text-sm px-4 py-3" data-testid="forgot-dev-code">
                  <span className="font-semibold">Test mode:</span> your code is <span className="font-mono font-bold tracking-widest">{devCode}</span>
                </div>
              )}
              <form onSubmit={doReset} className="space-y-4" data-testid="reset-form">
                <div>
                  <label className="text-sm font-medium">{t("auth.code")}</label>
                  <input data-testid="reset-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required
                    className="mt-1.5 w-full h-11 px-4 rounded-xl bg-card border border-border text-center font-mono tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-ring" placeholder="000000" />
                </div>
                <div>
                  <label className="text-sm font-medium">{t("auth.newPassword")}</label>
                  <input data-testid="reset-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6}
                    className="mt-1.5 w-full h-11 px-4 rounded-xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-ring" placeholder="At least 6 characters" />
                </div>
                {error && <div data-testid="reset-error" className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</div>}
                <button type="submit" disabled={loading || code.length !== 6} data-testid="reset-submit-btn"
                  className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("auth.resetBtn")} <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
            </>
          )}

          <Link to="/login" data-testid="forgot-back-login" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> {t("auth.backToLogin")}
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
